import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Zap, Plus, Edit2, Trash2, Play, Pause, Eye, 
    Loader, Search, Filter, CheckCircle, XCircle, Clock, AlertTriangle, Activity,
    Download, X, ChevronDown, ChevronRight
} from 'lucide-react';
import { apiClient } from '../../config/api';

const ClientGroupSelector = ({ groups, selectedId, onSelect }) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const filtered = groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));
    const selectedName = groups.find(g => g.id === selectedId)?.name || '';
    return (
        <div className="px-6 py-3 border-b bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Client/Group:</label>
                <div className="flex-1 relative">
                    <input
                        type="text"
                        value={open ? search : selectedName}
                        placeholder="Search client/group..."
                        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
                        onFocus={() => { setOpen(true); setSearch(''); }}
                        onBlur={() => setTimeout(() => setOpen(false), 200)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                    {open && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filtered.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
                            ) : filtered.map(g => (
                                <div
                                    key={g.id}
                                    onMouseDown={(e) => { e.preventDefault(); onSelect(g.id); setOpen(false); setSearch(''); }}
                                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-indigo-50 ${selectedId === g.id ? 'bg-indigo-100 font-medium text-indigo-700' : ''}`}
                                >
                                    {g.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminWorkflowsPage = ({ canEdit = true, canEditVb = true }) => {
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusSummary, setStatusSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Import modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [groups, setGroups] = useState([]);
    const [importGroupId, setImportGroupId] = useState('');
    const [contactPlans, setContactPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [selectedSteps, setSelectedSteps] = useState(new Set());
    const [expandedWorkflows, setExpandedWorkflows] = useState(new Set());
    const [importSearch, setImportSearch] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    useEffect(() => {
        loadWorkflows();
        loadStatusSummary();
    }, [statusFilter]);

    useEffect(() => {
        loadGroups();
    }, []);

    const loadWorkflows = async () => {
        setLoading(true);
        try {
            // Build query params properly
            const params = {};
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            // Pass params as query string, not as second argument
            const queryString = Object.keys(params).length > 0 
                ? '?' + new URLSearchParams(params).toString()
                : '';
            const result = await apiClient.get(`/workflows${queryString}`);
            setWorkflows(result.workflows || []);
        } catch (error) {
            console.error('Failed to load workflows:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStatusSummary = async () => {
        setLoadingSummary(true);
        try {
            const result = await apiClient.get('/workflows/status-summary');
            setStatusSummary(result);
        } catch (error) {
            console.error('Failed to load status summary:', error);
        } finally {
            setLoadingSummary(false);
        }
    };

    const deleteWorkflow = async (workflowId) => {
        if (!window.confirm('Are you sure you want to delete this workflow?')) {
            return;
        }

        try {
            await apiClient.delete(`/workflows/${workflowId}`);
            loadWorkflows();
            alert('Workflow deleted successfully');
        } catch (error) {
            alert('Failed to delete workflow: ' + (error.response?.data?.error || error.message));
        }
    };

    const toggleWorkflowStatus = async (workflowId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'paused' : 'active';
        try {
            await apiClient.put(`/workflows/${workflowId}`, { status: newStatus });
            loadWorkflows();
        } catch (error) {
            alert('Failed to update workflow status: ' + (error.response?.data?.error || error.message));
        }
    };

    const loadGroups = async () => {
        try {
            const result = await apiClient.get('/groups');
            setGroups(result || []);
        } catch (error) {
            console.error('Failed to load groups:', error);
        }
    };

    const loadContactPlansWithSteps = async (groupId) => {
        if (!groupId) return;
        setLoadingPlans(true);
        setContactPlans([]);
        setSelectedSteps(new Set());
        setExpandedWorkflows(new Set());
        try {
            const result = await apiClient.get(`/voicebox/groups/${groupId}/contact-plans-with-steps`);
            const plans = result.plans || [];
            setContactPlans(plans);
            // Auto-expand all workflows
            setExpandedWorkflows(new Set(plans.map(p => p.workflowId)));
        } catch (error) {
            console.error('Failed to load contact plans:', error);
            alert('Failed to load contact plans: ' + (error.message || 'Unknown error'));
        } finally {
            setLoadingPlans(false);
        }
    };

    const toggleStepSelection = (workflowId, triggerId) => {
        const key = `${workflowId}:${triggerId}`;
        setSelectedSteps(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const toggleWorkflowExpand = (workflowId) => {
        setExpandedWorkflows(prev => {
            const next = new Set(prev);
            if (next.has(workflowId)) {
                next.delete(workflowId);
            } else {
                next.add(workflowId);
            }
            return next;
        });
    };

    const selectAllSteps = () => {
        const all = new Set();
        for (const plan of filteredPlans) {
            for (const step of plan.steps) {
                all.add(`${plan.workflowId}:${step.triggerId}`);
            }
        }
        setSelectedSteps(all);
    };

    const deselectAllSteps = () => {
        setSelectedSteps(new Set());
    };

    const getSelectedStepDetails = () => {
        const details = [];
        for (const key of selectedSteps) {
            const [wId, tId] = key.split(':');
            const plan = contactPlans.find(p => String(p.workflowId) === wId);
            const step = plan?.steps.find(s => String(s.triggerId) === tId);
            if (plan && step) {
                details.push({
                    workflowId: plan.workflowId,
                    triggerId: step.triggerId,
                    workflowName: `VoiceBox: ${plan.title} - ${step.title}`,
                    parameters: step.parameters || {},
                    activationSourceId: step.activationSourceId || null,
                });
            }
        }
        return details;
    };

    const handleImportTriggers = async () => {
        const selected = getSelectedStepDetails();
        if (selected.length === 0) return;

        setImporting(true);
        setImportResult(null);
        try {
            const result = await apiClient.post(`/voicebox/groups/${importGroupId}/import-triggers`, {
                triggers: selected,
            });
            setImportResult(result);
            if (result.imported > 0) {
                loadWorkflows();
            }
        } catch (error) {
            console.error('Import failed:', error);
            setImportResult({ imported: 0, errors: [{ reason: error.message || 'Unknown error' }] });
        } finally {
            setImporting(false);
        }
    };

    // Filtered plans for the modal search
    const filteredPlans = importSearch
        ? contactPlans.filter(p =>
            p.title.toLowerCase().includes(importSearch.toLowerCase()) ||
            p.steps.some(s => s.title.toLowerCase().includes(importSearch.toLowerCase()))
        )
        : contactPlans;

    const getStatusBadge = (workflow) => {
        const status = workflow.status;
        const badges = {
            draft: { color: 'bg-gray-100 text-gray-800', icon: Edit2, label: 'Draft' },
            active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
            paused: { color: 'bg-yellow-100 text-yellow-800', icon: Pause, label: 'Paused' },
            archived: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Archived' }
        };

        const badge = badges[status] || badges.draft;
        const Icon = badge.icon;

        return (
            <div className="flex flex-col gap-1">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                    <Icon size={12} />
                    {badge.label}
                </span>
                {workflow.running_executions > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Activity size={10} className="animate-pulse" />
                        Running ({workflow.running_executions})
                    </span>
                )}
                {workflow.recent_failures > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertTriangle size={10} />
                        {workflow.recent_failures} failure{workflow.recent_failures > 1 ? 's' : ''} (24h)
                    </span>
                )}
                {workflow.last_execution_status === 'failed' && workflow.recent_failures === 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <AlertTriangle size={10} />
                        Last run failed
                    </span>
                )}
            </div>
        );
    };

    // Filter by search term only (status filter is handled by API)
    const filteredWorkflows = workflows.filter(workflow => {
        const matchesSearch = !searchTerm || 
            workflow.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            workflow.description?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    return (
        <div>
            <div className="mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <Zap size={24} />
                            Communication Workflows
                        </h1>
                        <p className="text-gray-600">
                            Create and manage automated communication workflows using drag-and-drop. 
                            Contact plans imported from VoiceBox will appear here as draft workflows.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate('/admin/workflow-builder')}
                            disabled={!canEdit}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            <Plus size={18} />
                            New Workflow
                        </button>
                        <button
                            onClick={() => {
                                navigate('/admin/workflow-builder?template=true');
                            }}
                            disabled={!canEdit}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            <Zap size={18} />
                            Create from Template
                        </button>
                    </div>
                </div>
            </div>

            {/* Status Summary */}
            {statusSummary && (
                <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Workflow Status Summary</h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">{statusSummary.total || 0}</div>
                            <div className="text-sm text-gray-600">Total Workflows</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">{statusSummary.active || 0}</div>
                            <div className="text-sm text-gray-600">Active</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                            <div className="text-2xl font-bold text-yellow-600">{statusSummary.paused || 0}</div>
                            <div className="text-sm text-gray-600">Paused</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600 flex items-center justify-center gap-1">
                                {statusSummary.running || 0}
                                {statusSummary.running > 0 && <Activity size={16} className="animate-pulse" />}
                            </div>
                            <div className="text-sm text-gray-600">Running</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                            <div className="text-2xl font-bold text-red-600 flex items-center justify-center gap-1">
                                {statusSummary.with_failures || 0}
                                {statusSummary.with_failures > 0 && <AlertTriangle size={16} />}
                            </div>
                            <div className="text-sm text-gray-600">With Failures</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Import VoiceBox Contact Plans */}
            <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Import VoiceBox Contact Plans</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Browse VoiceBox triggers and select individual steps to import as draft workflows.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowImportModal(true)}
                        disabled={!canEditVb}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                    >
                        <Download size={16} />
                        Browse &amp; Import
                    </button>
                </div>
            </div>

            {/* Import Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
                            <h2 className="text-lg font-semibold text-gray-900">Import VoiceBox Contact Plans</h2>
                            <button
                                onClick={() => { setShowImportModal(false); setImportGroupId(''); setContactPlans([]); setSelectedSteps(new Set()); setImportSearch(''); }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Client Selector with Search */}
                        <ClientGroupSelector
                            groups={groups}
                            selectedId={importGroupId}
                            onSelect={(id) => { setImportGroupId(id); loadContactPlansWithSteps(id); }}
                        />

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            {!importGroupId ? (
                                <div className="text-center py-12 text-gray-500">
                                    <Download size={40} className="mx-auto mb-3 text-gray-300" />
                                    <p>Select a client/group to load available contact plans.</p>
                                </div>
                            ) : loadingPlans ? (
                                <div className="text-center py-12">
                                    <Loader className="animate-spin mx-auto mb-3 text-indigo-600" size={32} />
                                    <p className="text-gray-600">Loading contact plans from VoiceBox...</p>
                                </div>
                            ) : contactPlans.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p>No active contact plans found for this client.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Search + Select All */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="relative flex-1">
                                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={importSearch}
                                                onChange={(e) => setImportSearch(e.target.value)}
                                                placeholder="Search triggers or steps..."
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                                            />
                                        </div>
                                        <button onClick={selectAllSteps} className="text-xs text-indigo-600 hover:underline whitespace-nowrap">Select All</button>
                                        <button onClick={deselectAllSteps} className="text-xs text-gray-500 hover:underline whitespace-nowrap">Clear</button>
                                    </div>

                                    <div className="text-xs text-gray-500 mb-3">
                                        {contactPlans.length} trigger workflow{contactPlans.length !== 1 ? 's' : ''} found, {contactPlans.reduce((s, p) => s + p.steps.length, 0)} total steps.
                                        {selectedSteps.size > 0 && (
                                            <span className="ml-2 text-indigo-600 font-medium">{selectedSteps.size} step{selectedSteps.size !== 1 ? 's' : ''} selected</span>
                                        )}
                                    </div>

                                    {/* Plans List */}
                                    <div className="space-y-2">
                                        {filteredPlans.map(plan => (
                                            <div key={plan.workflowId} className="border border-gray-200 rounded-lg overflow-hidden">
                                                {/* Trigger Workflow Header (not selectable) */}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleWorkflowExpand(plan.workflowId)}
                                                    className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center gap-3 text-left"
                                                >
                                                    {expandedWorkflows.has(plan.workflowId) ? (
                                                        <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-gray-900 text-sm truncate">{plan.title}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {plan.folderName && <span className="mr-3">Folder: {plan.folderName}</span>}
                                                            {plan.steps.length} step{plan.steps.length !== 1 ? 's' : ''}
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-gray-400 flex-shrink-0">ID: {plan.workflowId}</span>
                                                </button>

                                                {/* Steps (checkboxes) */}
                                                {expandedWorkflows.has(plan.workflowId) && plan.steps.length > 0 && (
                                                    <div className="divide-y divide-gray-100">
                                                        <div className="px-4 py-1.5 pl-10 bg-gray-50 flex items-center justify-end gap-2">
                                                            {(() => {
                                                                const allKeys = plan.steps.map(s => `${plan.workflowId}:${s.triggerId}`);
                                                                const allSelected = allKeys.every(k => selectedSteps.has(k));
                                                                return allSelected ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); setSelectedSteps(prev => { const next = new Set(prev); allKeys.forEach(k => next.delete(k)); return next; }); }}
                                                                        className="text-xs text-gray-500 hover:text-red-600 hover:underline"
                                                                    >Deselect all</button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); setSelectedSteps(prev => { const next = new Set(prev); allKeys.forEach(k => next.add(k)); return next; }); }}
                                                                        className="text-xs text-indigo-600 hover:underline font-medium"
                                                                    >Select all {plan.steps.length} steps</button>
                                                                );
                                                            })()}
                                                        </div>
                                                        {plan.steps.map(step => {
                                                            const stepKey = `${plan.workflowId}:${step.triggerId}`;
                                                            const isSelected = selectedSteps.has(stepKey);
                                                            return (
                                                                <label
                                                                    key={step.triggerId}
                                                                    className={`flex items-center gap-3 px-4 py-2.5 pl-10 cursor-pointer hover:bg-indigo-50 transition-colors ${
                                                                        isSelected ? 'bg-indigo-50' : ''
                                                                    }`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => toggleStepSelection(plan.workflowId, step.triggerId)}
                                                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                                    />
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="text-sm text-gray-800">{step.title}</div>
                                                                        <div className="text-xs text-gray-400">
                                                                            Workflow name: <span className="text-indigo-600 font-medium">VoiceBox: {plan.title} - {step.title}</span>
                                                                        </div>
                                                                    </div>
                                                                    <span className="text-xs text-gray-400 flex-shrink-0">#{step.triggerId}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {expandedWorkflows.has(plan.workflowId) && plan.steps.length === 0 && (
                                                    <div className="px-4 py-3 pl-10 text-sm text-gray-400 italic">No steps in this trigger workflow.</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Import Result */}
                        {importResult && (
                            <div className={`px-6 py-3 border-t text-sm ${importResult.imported > 0 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                {importResult.imported > 0 && (
                                    <div className="flex items-center gap-2 mb-1">
                                        <CheckCircle size={14} />
                                        <strong>{importResult.imported} workflow{importResult.imported !== 1 ? 's' : ''} imported successfully.</strong>
                                    </div>
                                )}
                                {importResult.skipped > 0 && (
                                    <div className="text-yellow-700">{importResult.skipped} skipped (already exist).</div>
                                )}
                                {importResult.errors && importResult.errors.length > 0 && importResult.errors.filter(e => e.reason && e.reason !== 'Already exists').length > 0 && (
                                    <div className="text-red-700 mt-1">
                                        Errors: {importResult.errors.filter(e => e.reason && e.reason !== 'Already exists').map(e => e.reason).join(', ')}
                                    </div>
                                )}
                                {importResult.workflows && importResult.workflows.length > 0 && (
                                    <div className="mt-1 text-green-700 text-xs">
                                        Created: {importResult.workflows.map(w => w.name).join(', ')}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between flex-shrink-0">
                            <div className="text-sm text-gray-600">
                                {selectedSteps.size > 0 ? (
                                    <span><strong>{selectedSteps.size}</strong> step{selectedSteps.size !== 1 ? 's' : ''} selected for import</span>
                                ) : (
                                    <span>Select steps to import as workflows</span>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowImportModal(false); setImportGroupId(''); setContactPlans([]); setSelectedSteps(new Set()); setImportSearch(''); setImportResult(null); }}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm"
                                >
                                    {importResult?.imported > 0 ? 'Close' : 'Cancel'}
                                </button>
                                <button
                                    disabled={selectedSteps.size === 0 || importing}
                                    onClick={handleImportTriggers}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                                >
                                    {importing && <Loader size={14} className="animate-spin" />}
                                    {importing ? 'Importing...' : `Import ${selectedSteps.size > 0 ? `${selectedSteps.size} Step${selectedSteps.size !== 1 ? 's' : ''}` : 'Selected'}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search workflows..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Statuses</option>
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Workflows List */}
            {loading ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Loader className="animate-spin mx-auto" size={32} />
                    <p className="text-gray-600 mt-4">Loading workflows...</p>
                </div>
            ) : filteredWorkflows.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Zap className="mx-auto text-gray-400" size={48} />
                    <p className="text-gray-600 mt-4">
                        {workflows.length === 0 
                            ? 'No workflows found. Create your first workflow to get started.'
                            : 'No workflows match your search criteria.'}
                    </p>
                    {workflows.length === 0 && (
                        <button
                            onClick={() => navigate('/admin/workflow-builder')}
                            className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mx-auto"
                        >
                            <Plus size={18} />
                            Create Workflow
                        </button>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Execution</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredWorkflows.map((workflow) => (
                                <tr key={workflow.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-gray-900 flex items-center gap-2">
                                            {workflow.name}
                                            {workflow.voicebox_contact_plan_id && (
                                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full" title="Imported from VoiceBox">
                                                    VB
                                                </span>
                                            )}
                                        </div>
                                        {workflow.description && (
                                            <div className="text-sm text-gray-500 mt-1">{workflow.description}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {workflow.group_name || 'All Groups'}
                                    </td>
                                    <td className="px-6 py-4">
                                        {getStatusBadge(workflow)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {workflow.last_execution_at ? (
                                            <div className="flex flex-col">
                                                <span>{new Date(workflow.last_execution_at).toLocaleDateString()}</span>
                                                <span className="text-xs text-gray-400">
                                                    {workflow.last_execution_status === 'completed' && (
                                                        <span className="text-green-600">✓ Success</span>
                                                    )}
                                                    {workflow.last_execution_status === 'failed' && (
                                                        <span className="text-red-600">✗ Failed</span>
                                                    )}
                                                    {workflow.last_execution_status === 'running' && (
                                                        <span className="text-blue-600">⟳ Running</span>
                                                    )}
                                                    {!workflow.last_execution_status && (
                                                        <span className="text-gray-400">Never run</span>
                                                    )}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">Never run</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                                        {workflow.workflow_type || 'email'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {new Date(workflow.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {(() => {
                                                const isVb = !!workflow.voicebox_contact_plan_id;
                                                const canEditThis = canEdit && (!isVb || canEditVb);
                                                return (
                                                    <>
                                                        <button
                                                            onClick={() => navigate(`/admin/workflow-builder/${workflow.id}`)}
                                                            className="text-blue-600 hover:text-blue-900"
                                                            title={canEditThis ? "Edit" : "View"}
                                                        >
                                                            {canEditThis ? <Edit2 size={18} /> : <Eye size={18} />}
                                                        </button>
                                                        {canEditThis && (workflow.status === 'active' ? (
                                                            <button
                                                                onClick={() => toggleWorkflowStatus(workflow.id, workflow.status)}
                                                                className="text-yellow-600 hover:text-yellow-900"
                                                                title="Pause"
                                                            >
                                                                <Pause size={18} />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => toggleWorkflowStatus(workflow.id, workflow.status)}
                                                                className="text-green-600 hover:text-green-900"
                                                                title="Activate"
                                                            >
                                                                <Play size={18} />
                                                            </button>
                                                        ))}
                                                        {canEditThis && (
                                                            <button
                                                                onClick={() => deleteWorkflow(workflow.id)}
                                                                className="text-red-600 hover:text-red-900"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminWorkflowsPage;

